import { useState, useEffect, useCallback } from 'react';
import { getCompanies } from '../services/companies.service';
import type Company from '../models/company';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import { dataCache, COMPANIES_CACHE_KEY } from '../../../core/utils/data-cache';

interface CompaniesState {
  readonly initialized: boolean;
  readonly companies?: Company[];
  readonly error?: boolean;
}

/**
 * Returns the list of companies for super-admin management.
 *
 * Mirrors `useAdminOrAuthorOffices`: the response is stored in an in-memory
 * cache so navigating away from and back to "Companies" renders data
 * immediately (stale-while-revalidate).  A background fetch always runs on
 * mount to keep the cache fresh.  The cache is automatically invalidated
 * whenever a company is created, updated or deleted (see `companies.service.ts`).
 *
 * @param version - Increment this value to force an explicit cache flush and
 *   re-fetch.  Defaults to 0.
 */
export const useCompanies = (version = 0): CompaniesState => {
  // Initialise from cache for instant rendering on subsequent visits.
  const [state, setState] = useState<CompaniesState>(() => {
    const cached = dataCache.get<Company[]>(COMPANIES_CACHE_KEY);
    return cached != null
      ? { initialized: true, companies: cached }
      : { initialized: false };
  });

  const keycloak = useKeycloak();

  useEffect(() => {
    let cancelled = false; // Prevent state updates after unmount.

    // When an explicit refresh is requested (version > 0), show the loading
    // indicator immediately so the user knows something is happening.
    if (version > 0) {
      setState({ initialized: false });
    }

    const getAllCompanies = async (): Promise<void> => {
      try {
        const companies = (await getCompanies(keycloak)) ?? [];
        if (!cancelled) {
          // Refresh the cache with the latest server response.
          dataCache.set(COMPANIES_CACHE_KEY, companies);
          setState({ initialized: true, companies });
        }
      } catch (error) {
        // Only mark as errored when there is no cached data to fall back on.
        if (!cancelled && !dataCache.get(COMPANIES_CACHE_KEY)) {
          setState({ initialized: false, error: true });
        }
      }
    };

    // Always revalidate in the background so the list stays up-to-date even
    // when cached data was served immediately.
    getAllCompanies();

    return (): void => {
      cancelled = true;
    };
  }, [keycloak, version]);

  return state;
};

interface Props {
  readonly id: string | undefined;
}

interface CompanyState {
  readonly initialized: boolean;
  readonly company?: Company;
  readonly error?: boolean;
}

/**
 * Returns a single company by id, resolved from the cached companies list.
 *
 * The company detail is not fetched individually; instead it is derived from
 * the companies list (seeded from cache / fetched on mount).  When `id` is
 * empty a fresh, empty company is returned for the create flow.
 */
export const useCompany = ({ id }: Props): CompanyState & { refresh: () => void } => {
  const [version, setVersion] = useState(0);
  const companiesState = useCompanies(version);

  const company = ((): Company | undefined => {
    if (id === '' || !id) {
      return undefined;
    }
    return companiesState.companies?.find((c) => c.id === id);
  })();

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  return {
    initialized: companiesState.initialized,
    company,
    error: companiesState.error,
    refresh,
  };
};
